import React, { useState, useEffect } from "react";
import {
  X,
  Zap,
  Sunrise,
  SunMedium,
  BellOff,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Clock,
  ShieldAlert,
} from "lucide-react";
import { UserProfile, NotificationPreference } from "../types";
import { updateNotificationPreference } from "../services/auth";

export interface NotificationPreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: UserProfile;
  targetUser?: UserProfile;
  onSaved?: (newPreference: NotificationPreference) => void;
}

interface PreferenceOption {
  id: NotificationPreference;
  title: string;
  badge: string;
  badgeColor: string;
  icon: React.ReactNode;
  description: string;
}

const PREFERENCE_OPTIONS: PreferenceOption[] = [
  {
    id: "realtime",
    title: "Real-Time Alerts",
    badge: "Instant",
    badgeColor: "bg-amber-50 text-amber-700 border-amber-200",
    icon: <Zap className="w-5 h-5 text-amber-500 shrink-0" />,
    description:
      "Instant email notifications whenever an issue is created, assigned to you, updated in status, or commented on.",
  },
  {
    id: "daily",
    title: "Once a Day Digest",
    badge: "8:00 AM CT",
    badgeColor: "bg-sky-50 text-sky-700 border-sky-200",
    icon: <Sunrise className="w-5 h-5 text-sky-500 shrink-0" />,
    description:
      "One daily summary email delivered at 8:00 AM Chicago time covering all activity since your last digest.",
  },
  {
    id: "twice_daily",
    title: "Twice a Day Digest",
    badge: "8:00 AM & 12:00 PM CT",
    badgeColor: "bg-teal-50 text-teal-700 border-teal-200",
    icon: <SunMedium className="w-5 h-5 text-[#2ba5b5] shrink-0" />,
    description:
      "Morning (8:00 AM) and midday (12:00 PM) summary emails bundling all updates across your active tickets.",
  },
  {
    id: "never",
    title: "Never (In-App Only)",
    badge: "No Emails",
    badgeColor: "bg-slate-100 text-slate-600 border-slate-200",
    icon: <BellOff className="w-5 h-5 text-slate-400 shrink-0" />,
    description:
      "Disable all automated email notifications. Monitor issues and reply to discussions directly in the web app.",
  },
];

export const NotificationPreferencesModal: React.FC<
  NotificationPreferencesModalProps
> = ({ isOpen, onClose, currentUser, targetUser, onSaved }) => {
  const effectiveUser = targetUser || currentUser;
  const isEditingOther = !!targetUser && targetUser.uid !== currentUser.uid;

  const [selectedPreference, setSelectedPreference] =
    useState<NotificationPreference>(
      effectiveUser.notificationPreference || "realtime",
    );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    setSelectedPreference(effectiveUser.notificationPreference || "realtime");
    setError(null);
    setSuccessMessage(null);
  }, [effectiveUser.uid, effectiveUser.notificationPreference, isOpen]);

  if (!isOpen) return null;

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await updateNotificationPreference(effectiveUser.uid, selectedPreference);
      setSuccessMessage("Notification preferences saved successfully.");
      if (onSaved) {
        onSaved(selectedPreference);
      }
      setTimeout(() => {
        onClose();
      }, 700);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Failed to update preferences.";
      setError(msg);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="notification-preferences-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-xs animate-in fade-in duration-150"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50/60">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-teal-50 border border-teal-200 flex items-center justify-center text-[#2ba5b5]">
              <SunMedium className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2
                  id="notification-preferences-title"
                  className="text-base font-bold text-slate-900"
                >
                  Notification Preferences
                </h2>
                {isEditingOther && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-700">
                    Admin Override
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500">
                {isEditingOther && effectiveUser.displayName
                  ? `${effectiveUser.displayName} (${effectiveUser.email})`
                  : effectiveUser.email}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close notification preferences modal"
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto space-y-4 text-xs">
          {error && (
            <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {successMessage && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}

          <p className="text-slate-600">
            Choose how you would like to receive email updates regarding ticket
            assignments, status changes, and discussion replies:
          </p>

          {/* Radio Cards */}
          <div className="space-y-2.5">
            {PREFERENCE_OPTIONS.map((opt) => {
              const isSelected = selectedPreference === opt.id;
              return (
                <label
                  key={opt.id}
                  className={`flex items-start gap-3.5 p-3.5 rounded-xl border transition cursor-pointer ${
                    isSelected
                      ? "bg-teal-50/50 border-[#2ba5b5] shadow-xs"
                      : "bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="notificationPreference"
                    value={opt.id}
                    checked={isSelected}
                    onChange={() => setSelectedPreference(opt.id)}
                    className="mt-0.5 text-[#2ba5b5] focus:ring-[#2ba5b5]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2">
                        {opt.icon}
                        <span className="font-semibold text-slate-800 text-xs">
                          {opt.title}
                        </span>
                      </div>
                      <span
                        className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${opt.badgeColor}`}
                      >
                        {opt.badge}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      {opt.description}
                    </p>
                  </div>
                </label>
              );
            })}
          </div>

          {/* Timezone & Weekday Disclaimer */}
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-1 text-slate-500">
            <div className="flex items-center gap-1.5 font-semibold text-slate-700 text-[11px]">
              <Clock className="w-3.5 h-3.5 text-teal-600 shrink-0" />
              <span>Chicago Business Hours Schedule (CT)</span>
            </div>
            <p className="text-[11px] leading-relaxed">
              Digest emails are dispatched Monday through Friday at 8:00 AM and
              12:00 PM America/Chicago time. Any activity over the weekend is
              bundled into Monday morning&apos;s 8:00 AM digest.
            </p>
          </div>

          {/* Critical Operational SLA Note */}
          <div className="p-3 bg-amber-50/60 border border-amber-200 rounded-xl flex items-start gap-2 text-[11px] text-amber-800 leading-relaxed">
            <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <span>
              <strong>Note:</strong> Urgent on-call SLA warnings and breach
              alerts bypass digests to protect response times for critical
              application incidents.
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2.5 px-6 py-4 border-t border-slate-200 bg-slate-50/60">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-200/60 rounded-xl transition cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex items-center gap-1.5 px-5 py-2 text-xs font-semibold text-white bg-[#2ba5b5] hover:bg-[#238a97] rounded-xl shadow-xs transition disabled:opacity-50 cursor-pointer"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <span>Save Preferences</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
